import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GetTokens, isTokenValid } from '../../utils/axiosInstance';
import { Me } from '../../utils/Interface';
import { Responsive } from '../Interface/Pages';
import style from './sidebar.module.css';
import {
  BuildingLibraryIcon,
  Squares2X2Icon,
  ChartBarIcon,
  CalculatorIcon,
  BanknotesIcon,
  DocumentTextIcon,
  ReceiptPercentIcon,
  DocumentCheckIcon,
  ChartBarSquareIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { Collapse } from '@mui/material';
import { ChurchLogo } from '../../assets/ChurchLogo';

interface ISidebar extends Responsive {
  hidden?: Boolean | undefined;
}

interface SideLinkBase {
  icon: ReactNode;
  title: string;
}

interface SideLink extends SideLinkBase {
  path: string;
  activePath: string;
}
interface SideDropdown extends SideLinkBase {
  links: ReactElement[];
}

const SideLink = ({ icon, title, path, activePath }: SideLink) => {
  const active = path === activePath ? style.active : style.inactive;
  return (
    <div className={`${style.link} ${active}`}>
      <div className={style.icon}>{icon}</div>
      <Link className={style.title} to={`/${path}`}>
        {title}
      </Link>
    </div>
  );
};

const SideDropdown = ({ icon, title, links }: SideDropdown) => {
  const [isDropped, setIsDropped] = useState(false);

  return (
    <div className={style.dropdown}>
      <div className={style.link}>
        <button
          onClick={() => {
            setIsDropped((prev) => !prev);
          }}
        >
          <div className={style.icon}>{icon}</div>
          <a className={style.title}>
            {title}
            <div className={`${isDropped ? style.arrUnhidden : style.arrHidden} ml-auto mr-1`}>
              <i className="fas fa-chevron-right"></i>
            </div>
          </a>
        </button>
      </div>
      <Collapse in={isDropped}>
        <div className={`${style.dropdownLinks}`}>{links.map((link) => link)}</div>
      </Collapse>
    </div>
  );
};

type TempSideLinkType = {
  onClick: () => void;
} & SideLinkBase;

const TempSideLink = ({ icon, title, onClick }: TempSideLinkType) => (
  <div className={`${style.tempLink}`}>
    <div className={style.icon}>{icon}</div>
    <button className={style.title} onClick={onClick}>
      {title}
    </button>
  </div>
);

const Sidebar = ({ view, expander, hidden }: ISidebar) => {
  const location = useLocation();
  const path = location.pathname.split('/')[1];
  const [expand, setExpand] = useState(true);
  const [myAccInfo, setMyAccInfo] = useState<Me | null>(null);

  const newView = view ? style.hide : style.show;
  const newExpand = expand ? style.mainExpand : style.mainCollapse;

  const OnExpand = () => {
    setExpand((prev) => !prev);
    expander && expander();
  };

  const [isChurchEnable, setIsChurchEnable] = useState(false);

  const SetMe = async () => {
    const tokens = GetTokens();
    if (isTokenValid(tokens)) {
      if (tokens.role === 'superuser') {
        setIsChurchEnable(true);
      }
      setMyAccInfo(tokens);
    }
  };

  useEffect(() => {
    SetMe();
  }, []);

  return !hidden ? (
    <div className={`${style.container} ${newView}`}>
      <div className={`${style.main} ${newExpand}`}>
        <div className={style.header}>
          <div className="m-6 flex items-center gap-4">
            <ChurchLogo />
            <h1 className="text-white text-start text-2xl font-bold font-inter">
              PARISH <br /> ACCOUNTING
            </h1>
          </div>
        </div>
        <div className={style.listLink}>
          <SideLink activePath={path} icon={<BuildingLibraryIcon width={24} />} path={'home'} title={'Dashboard'} />
          {isChurchEnable && <SideLink activePath={path} icon={<Squares2X2Icon width={24} />} path={'churches'} title={'Parishes'} />}
          <SideDropdown
            links={[
              <SideLink
                key={1}
                activePath={path}
                icon={<BanknotesIcon width={24} />}
                path={'income-and-expense'}
                title={'Income and Expense'}
              />,
              <SideLink key={2} activePath={path} icon={<DocumentTextIcon width={24} />} path={'balance-sheet'} title={'Balance Sheet'} />,
            ]}
            icon={<ChartBarIcon width={24} />}
            title={'Chart of Accounts'}
          />
          <SideDropdown
            links={[
              <SideLink
                key={3}
                activePath={path}
                icon={<ReceiptPercentIcon width={24} />}
                path={'cash-receipts'}
                title={'Cash Receipts'}
              />,
              <SideDropdown
                key={4}
                links={[
                  <SideLink
                    key={5}
                    activePath={path}
                    icon={<i className="fa-solid fa-money-check-dollar"></i>}
                    path={'voucher'}
                    title={'Cheque Voucher'}
                  />,
                  <SideLink
                    key={6}
                    activePath={path}
                    icon={<i className="fa-solid fa-newspaper"></i>}
                    path={'journal-voucher'}
                    title={'Journal Voucher'}
                  />,
                  <SideLink
                    key={7}
                    activePath={path}
                    icon={<i className="fa-solid fa-money-bill"></i>}
                    path={'petty-cash-voucher'}
                    title={'Petty Cash Voucher'}
                  />,
                ]}
                icon={<DocumentCheckIcon width={24} />}
                title={'Vouchers'}
              />,
              <SideLink key={8} activePath={path} icon={<ChartBarSquareIcon width={24} />} path={'reports'} title={'Reports'} />,
              <SideLink key={9} activePath={path} icon={<FolderIcon width={24} />} path={'e-files'} title={'E-files'} />,
            ]}
            icon={<CalculatorIcon width={24} />}
            title={'Accounting'}
          />
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default Sidebar;
